import React from "react";
import { CalendarProps, EventWrapperProps } from "react-big-calendar";
import styled, { css } from "styled-components";
import CalendarTask from "@lifeat/shared/calendar/impl/CalendarTask";
import { DEFAULT_TAG_PALETTE, TAG_PALETTES } from "@lifeat/shared/task-management/TaskTagConstants";
import { ContextMenuComponents } from "@common/LifeAtContextMenu/ContextMenuComponents";
import { useAppearanceContext } from "@contexts/AppearanceContext";
import LifeAtCalendarEventColors, { IntegrationCalendarColorMap } from "@themes/CalendarStyles";
import { FontSize } from "@themes/Fonts";
import { getTextColor } from "@util/StyleUtil";
import { useTaskListContext } from "../../../../features/task-management/TaskListContext";

const DELETE_LABEL = "Delete";

const EventWrapper = styled.div<{ backgroundColor: string; textColor: string; addMargin: boolean }>`
    .rbc-event {
        background-color: ${(props) => props.backgroundColor};
        border: 1px solid ${(props) => props.theme.backgroundPrimary};

        flex-direction: column-reverse;
        margin-left: ${(props) => (props.addMargin ? `8px` : `0`)};

        .rbc-event-label {
            display: none;
        }

        * > div {
            color: ${(props) => props.textColor};
        }
        .calendar-task-icon {
            fill: ${(props) => props.textColor};
        }
        .styled-checkbox {
            visibility: visible;
            scale: 0.9;
            margin-right: unset;
            ${(props) =>
                props.textColor &&
                css`
                    border-color: ${props.textColor};
                `};
        }
        .checkbox-checkmark {
            ${(props) =>
                props.textColor &&
                css`
                    stroke: ${props.textColor} !important;
                `};
        }
    }
`;

const DropdownItemContainer = styled.div`
    display: flex;
    width: 100%;
    align-items: center;

    color: ${(props) => props.theme.textColorPrimary};

    font-size: ${FontSize.normal};
    padding: 10px;

    cursor: pointer;

    &:hover {
        background-color: ${(props) => props.theme.hoverPrimary};
    }
`;

type CalendarEventWrapperProps = EventWrapperProps & CalendarProps;

function CalendarEventWrapper(props: CalendarEventWrapperProps) {
    const { lightMode } = useAppearanceContext();
    const { deleteTask } = useTaskListContext();

    const eventResource = props.event.resource;

    let backgroundColor = LifeAtCalendarEventColors.blue.background;
    let textColor = LifeAtCalendarEventColors.blue.text;

    if (eventResource instanceof CalendarTask) {
        const palette = TAG_PALETTES.find((c) => c.primary === eventResource.tagColorHex) ?? DEFAULT_TAG_PALETTE;
        backgroundColor = lightMode ? palette.lighter : palette.darker;
        textColor = lightMode ? palette.darker : palette.lighter;
    } else if (eventResource?.colorHex) {
        // TODO: checking for `eventResource instanceof CalendarEvent` here didnt work, not sure why
        const colorFromMap = IntegrationCalendarColorMap[eventResource.colorHex];
        backgroundColor = colorFromMap?.background ?? props.event?.resource?.colorHex;
        textColor = colorFromMap?.text ?? getTextColor(props.event?.resource?.colorHex);
    }

    // Note: When dropping a task on the all day gutter, an extra empty event is created.
    // This is to prevent rendering that empty event.
    if (!eventResource) {
        return null;
    }

    return (
        <ContextMenuComponents.Root>
            <ContextMenuComponents.Trigger>
                <EventWrapper
                    className={`calendar-event-wrapper`}
                    backgroundColor={backgroundColor}
                    textColor={textColor}
                    addMargin={!eventResource.isAllDayEvent}
                >
                    {props.children}
                </EventWrapper>
            </ContextMenuComponents.Trigger>
            {eventResource instanceof CalendarTask && eventResource.itemSource === "lifeat-tasks" && (
                <ContextMenuComponents.Content>
                    <DropdownItemContainer
                        onClick={(e) => {
                            e.stopPropagation();
                            deleteTask(eventResource.id);
                        }}
                    >
                        {DELETE_LABEL}
                    </DropdownItemContainer>
                </ContextMenuComponents.Content>
            )}
        </ContextMenuComponents.Root>
    );
}

export default CalendarEventWrapper;
